import React, { useState, useEffect, useRef } from "react";
import LogoutButton from "../Auth/LogoutButton";
import { API_URL } from "../ConstVariable";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import LogRecord, { getServiceColorAndBorder } from "../Components/ConversationWidget/LogRecord";
import { 
  Snackbar, 
  Alert, 
  FormControl, 
  Select, 
  MenuItem, 
  Checkbox, 
  ListItemText, 
  OutlinedInput,
  ClickAwayListener
} from "@mui/material";

const LogSearch = ({ conversationIdFromUrl }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [conversationId, setConversationId] = useState(
    conversationIdFromUrl || ""
  );
  const [fetchedLogs, setFetchedLogs] = useState(null);
  const { user } = useAuth0();
  const developer = user.name;
  const role = user?.oi_profile;

  // State for Snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [selectedServices, setSelectedServices] = useState([]);
  const [services, setServices] = useState([]);
  const [isServicesLoading, setIsServicesLoading] = useState(false);
  const [isLogsLoading, setIsLogsLoading] = useState(false);
  const [openSelect, setOpenSelect] = useState(false);
  const isMouseClickedOnce = useRef(false);

  useEffect(() => {
    if (role === "internal" && conversationIdFromUrl) {
      setConversationId(conversationIdFromUrl);
    }
  }, [role, conversationIdFromUrl]);

  useEffect(() => {
    if (
      role === "internal" &&
      conversationId &&
      selectedServices.includes("conversation-engine")
    ) {
      handleLogSearchClickButton();
    }
  }, [conversationId]);

  useEffect(() => {
    setOpenSelect(false);
    setFetchedLogs([]);
    setServices([]);
    setSelectedServices([]);
    if (!conversationId || conversationId.trim() === "") {
      return;
    }
    setIsServicesLoading(true);
    let active = true;

    const delayDebounceFn = setTimeout(async () => {
      try {
        const token = await getAccessTokenSilently();
        if (!active) return;

        isMouseClickedOnce.current = false;
        const api_path = `${API_URL}/oi/api/v1/services/${conversationId}`;
        const response = await axios.get(api_path, {
          headers: {
            Authorization: `Bearer ${token}`
          },
          withCredentials: true
        });

        if (!active) return;

        if (response.status === 200 && Array.isArray(response.data)) {
          const sorted = [...response.data].sort((a, b) => a.localeCompare(b));
          setServices(sorted);
          if (sorted.includes("conversation-engine")) {
            setSelectedServices(["conversation-engine"]);
          } else {
            setSelectedServices([]);
          }
          if (sorted.length === 0) {
            setFetchedLogs([]);
          }
        } else {
          setServices([]);
          setSelectedServices([]);
          setFetchedLogs([]);
        }
      } catch (error) {
        if (active) {
          console.error("Failed to fetch services:", error);
          setServices([]);
          setSelectedServices([]);
          setFetchedLogs([]);
        }
      } finally {
        if (active) {
          setIsServicesLoading(false);
        }
      }
    }, 1000);

    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
    };
  }, [conversationId, getAccessTokenSilently]);

  const handleLogSearchClickButton = async () => {
    setOpenSelect(false);
    setFetchedLogs([]);
    setIsLogsLoading(true);
    if (!isMouseClickedOnce.current) {
      isMouseClickedOnce.current = true;
    }
    const servicesQuery = selectedServices.length > 0 ? `?services=${selectedServices.join(',')}` : '';
    const api_path = `${API_URL}/oi/api/v1/logs/${conversationId}${servicesQuery}`;
    try {
      const response = await axios.get(api_path, {
        headers: {
          Authorization: `Bearer ${await getAccessTokenSilently()}`
        },
        withCredentials: true
      });
      if (response.status === 200) {
        setFetchedLogs(response.data);
      }
    } catch (error) {
      console.error("Failed to fetch the logs, cause:", error);
      setSnackbarMessage("Failed to fetch logs. Please try again.");
      setOpenSnackbar(true);
      setFetchedLogs([]);
    } finally {
      setIsLogsLoading(false);
    }
  };

  const handleConversationIdSelection = (e) => {
    setOpenSelect(false);
    isMouseClickedOnce.current = false;
    const val = e?.target?.value;
    setConversationId(val);
    if (!val || val.trim() === "") {
      setFetchedLogs([]);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: "white",
          height: "50px",
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            marginRight: "10px",
          }}
        >
          <span>{developer}</span>
          <LogoutButton />
        </div>
      </div>
      <div
        style={{
          padding: "5px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            width: "50%",
          }}
        >
          <p style={{ fontWeight: 500, fontSize: "22px" }}>
            Enter Conversation ID
          </p>
          <div>
            <input
              style={{
                width: "40%",
                borderRadius: "10px",
                fontSize: "16px",
                marginRight: "20px",
                backgroundColor: "white",
              }}
              value={conversationId || ""}
              onChange={handleConversationIdSelection}
              type="text"
            />
            
            <ClickAwayListener onClickAway={() => setOpenSelect(false)}>
              <FormControl 
                sx={{ m: 0, minWidth: 220, verticalAlign: 'middle', marginRight: '20px' }}
                onMouseDownCapture={(e) => {
                  // If clicking inside the opened menu paper, let it pass through normally (checkboxes)
                  if (e.target.closest('.MuiMenu-paper')) {
                    return;
                  }
                  // If clicking the Select box while open, toggle it closed and stop event propagation
                  if (openSelect) {
                    setOpenSelect(false);
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
              >
                <Select
                  multiple
                  displayEmpty
                  open={openSelect}
                  onOpen={() => setOpenSelect(true)}
                  onClose={(e, reason) => {
                    // Only close via escape key natively, ClickAwayListener handles the rest
                    if (reason === 'escapeKeyDown') {
                      setOpenSelect(false);
                    }
                  }}
                  value={selectedServices}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedServices(
                      typeof value === 'string' ? value.split(',') : value,
                    );
                  }}
                  input={<OutlinedInput style={{ height: '40px', borderRadius: '10px' }} />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      if (services.length > 0) {
                        return <span style={{ color: "#d91f06ff", fontWeight: "bold" }}>Select a service</span>;
                      }
                      return <span style={{ color: "#aaa" }}>Services</span>;
                    }

                    if (selected.length === 1) {
                      return selected[0];
                    }

                    return `${selected.length} Services Selected`;
                  }}
                  disabled={isServicesLoading}
                  style={{
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#fff'
                  }}
                  MenuProps={{
                    disablePortal: true, // Crucial: Keeps the dropdown inside the ClickAwayListener DOM tree
                    BackdropProps: {
                      style: { pointerEvents: 'none' } // Lets clicks pass immediately through to the text input
                    },
                    disableScrollLock: true,
                    disableAutoFocusItem: true,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    PaperProps: { 
                      style: { 
                        maxHeight: 300,
                        marginTop: '4px',
                        pointerEvents: 'auto' // Re-enables clicking for menu items
                      } 
                    },
                    MenuListProps: {
                      onMouseLeave: () => setOpenSelect(false),
                      onWheel: () => setOpenSelect(false)
                    }
                  }}
                >
                  {isServicesLoading ? (
                    <MenuItem disabled value="">
                      <em>Loading services...</em>
                    </MenuItem>
                  ) : services.length > 0 ? (
                    services.map((serviceName) => {
                      const { bg: bgColor, border: borderColor } = getServiceColorAndBorder(serviceName, services);
                      return (
                        <MenuItem
                          key={serviceName}
                          value={serviceName}
                          style={{
                            backgroundColor: bgColor,
                            borderLeft: `4px solid ${borderColor}`,
                            margin: '2px 6px',
                            borderRadius: '4px'
                          }}
                        >
                          <Checkbox checked={selectedServices.indexOf(serviceName) > -1} />
                          <ListItemText primary={serviceName} />
                        </MenuItem>
                      );
                    })
                  ) : (
                    <MenuItem disabled value="">
                      <em>No services found</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </ClickAwayListener>

            <button
              disabled={isLogsLoading || selectedServices.length === 0}
              onClick={handleLogSearchClickButton}
              style={{
                padding: "8px 15px",
                borderRadius: "12px",
                fontSize: "18px",
                fontWeight: "500",
                background: "linear-gradient(135deg, #6366f1, #4f46e5)",
                color: "#fff",
                border: "none",
                boxShadow: (isLogsLoading || selectedServices.length === 0) ? "none" : "0 4px 10px rgba(0, 0, 0, 0.15)",
                cursor: (isLogsLoading || selectedServices.length === 0) ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                transform: "scale(1)",
                opacity: (isLogsLoading || selectedServices.length === 0) ? 0.6 : 1,
              }}
              onMouseOver={(e) => {
                if (!(isLogsLoading || selectedServices.length === 0)) {
                  e.currentTarget.style.opacity = "0.9";
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = (isLogsLoading || selectedServices.length === 0) ? "0.6" : "1";
                e.currentTarget.style.transform = "scale(1)";
              }}
              onMouseDown={(e) => {
                if (!(isLogsLoading || selectedServices.length === 0)) {
                  e.currentTarget.style.transform = "scale(0.95)";
                }
              }}
              onMouseUp={(e) => {
                if (!(isLogsLoading || selectedServices.length === 0)) {
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              {isLogsLoading ? "⌛ Searching..." : "🔍 Search"}
            </button>
          </div>
        </div>
      </div>
      <hr style={{ width: "90%" }} />

      <div style={{ overflowY: "auto", height: "80vh" }}>
        {
          isLogsLoading && isMouseClickedOnce.current === true ? "Fetching the Logs"
            : fetchedLogs?.length > 0
              ? fetchedLogs.map((singleLog, index) => (
                <LogRecord key={index} log={singleLog} services={services} />
              ))
              : isMouseClickedOnce.current === false || isServicesLoading
                ? ""
                : "No Logs Found"
        }
      </div>

      {/* Snackbar for error */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default LogSearch;